import { Link } from '@mui/material';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { Citation } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { buildCitationSentenceSegments, isHttpsCitationUrl } from './citations';
import useStyles from './MessageBubble.styles';

const TRANSLATION_KEYS = {
  groupAriaLabel: 'chat.citations.groupAriaLabel',
  sentencePrefix: 'chat.citations.sourcesSentencePrefix',
  conjunction: 'chat.citations.sourcesSentenceConjunction',
} as const;

type MessageCitationsProps = {
  /** Source references attached to an assistant message, if any. */
  citations?: ReadonlyArray<Citation>;
};

/**
 * Renders the sources sentence for an assistant message, e.g.
 * "For more details, check out the A, B, and C." Each citation
 * `displayLabel` is a link when it has an HTTPS `url`, otherwise plain
 * text. Returns `null` when there are no renderable citations.
 * @param citations - Source references attached to the assistant message.
 */
const MessageCitations = ({ citations }: MessageCitationsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  const segments = buildCitationSentenceSegments(
    citations,
    t(TRANSLATION_KEYS.conjunction)
  );
  if (segments.length === 0) {
    return null;
  }

  return (
    <p
      aria-label={t(TRANSLATION_KEYS.groupAriaLabel)}
      className={classes.citations}
      data-testid="ai-sidekick-message-citations"
      role="note"
    >
      {t(TRANSLATION_KEYS.sentencePrefix)}{' '}
      {segments.map(({ citation, separatorBefore }) => (
        <Fragment key={citation.index}>
          {separatorBefore}
          {citation.url !== undefined && isHttpsCitationUrl(citation.url) ? (
            <Link
              className={classes.citationLink}
              data-testid="ai-sidekick-message-citation-link"
              href={citation.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              {citation.displayLabel}
            </Link>
          ) : (
            <span
              className={classes.citationItem}
              data-testid="ai-sidekick-message-citation-text"
            >
              {citation.displayLabel}
            </span>
          )}
        </Fragment>
      ))}
      {'.'}
    </p>
  );
};

export default MessageCitations;
